<script setup>
import { computed, nextTick, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import { useKanbanFunnelAdmin } from 'dashboard/composables/useKanbanFunnelAdmin';
import { useFocusTrap } from '../composables/useFocusTrap';
import KanbanStageList from './KanbanStageList.vue';

// Configuração de funis e etapas (T3.5). Antes desta unidade o funil só
// nascia por rake e nenhuma etapa podia ser criada, editada, reordenada ou
// removida pela tela.
//
// Diálogo sobre o board, e não uma rota nova: quem configura o funil está
// olhando para ele, e voltar ao board com os filtros e a rolagem intactos faz
// parte da tarefa. O mesmo diálogo com foco preso, Escape e devolução de foco
// do KanbanConversationModal (useFocusTrap compartilhado).
//
// ESCRITA É DE ADMINISTRADOR (DebtKanbanPolicy#manage?, 401 para agente): quem
// monta este diálogo (KanbanBoardIndex.vue) só oferece o gatilho ao
// administrador — a tela não pode ter botão que sempre falha.
const emit = defineEmits(['close']);

const { t } = useI18n();

const {
  funnels,
  isLoading,
  hasLoadError,
  isSubmitting,
  selectedFunnelId,
  selectedFunnel,
  load,
  selectFunnel,
  createFunnel,
  renameFunnel,
  archiveFunnel,
  unarchiveFunnel,
  deleteFunnel,
  createStage,
  updateStage,
  deleteStage,
  reorderStages,
} = useKanbanFunnelAdmin();

const dialogRef = ref(null);
const bodyRef = ref(null);
const newFunnelName = ref('');
const renameDraft = ref('');
const errorKey = ref(null);
const errorCardsCount = ref(null);
// O board só recarrega funis e colunas se alguma coisa MUDOU: abrir e fechar
// a configuração sem escrever nada não deve custar um refetch do board.
const hasChanged = ref(false);

const close = () => emit('close', hasChanged.value);

useFocusTrap(dialogRef, { onEscape: close });

// O campo de renomear nascia VAZIO com um funil já selecionado: `load()`
// escolhe o primeiro funil, mas só `onSelectFunnel` preenchia o rascunho —
// quem abria a configuração via um campo em branco rotulado "Nome do funil",
// que se lê como "o funil não tem nome". Preencher aqui cobre a entrada por
// carga; as entradas por seleção e por criação já preenchiam.
onMounted(async () => {
  await load();
  renameDraft.value = selectedFunnel.value?.name ?? '';
});

const titleId = 'kanban-funnel-manager-title';

const clearError = () => {
  errorKey.value = null;
  errorCardsCount.value = null;
};

const applyResult = result => {
  if (result.ok) {
    hasChanged.value = true;
    clearError();
    return true;
  }
  errorKey.value = result.errorKey;
  errorCardsCount.value = result.cardsCount;
  return false;
};

// As ações de etapa passam pelo mesmo registro de mudança antes de descerem
// para a lista: sem isto, criar uma etapa e fechar deixaria o board sem a
// coluna nova até um F5.
const trackStageAction =
  action =>
  async (...args) => {
    const result = await action(...args);
    if (result?.ok) hasChanged.value = true;
    return result;
  };

const onCreateStage = trackStageAction(createStage);
const onUpdateStage = trackStageAction(updateStage);
const onDeleteStage = trackStageAction(deleteStage);
const onReorderStages = trackStageAction(reorderStages);

const onSelectFunnel = funnel => {
  clearError();
  selectFunnel(funnel.id);
  renameDraft.value = funnel.name;
};

const canCreateFunnel = computed(
  () => newFunnelName.value.trim().length > 0 && !isSubmitting.value
);

// Funil novo nasce SEM etapa e um funil sem etapa é inerte (zero coluna no
// board). Por isso o foco vai para a criação da primeira etapa: a tela conduz
// ao passo que falta em vez de deixar um funil vazio parecendo pronto.
const focusFirstStageAction = async () => {
  await nextTick();
  bodyRef.value
    ?.querySelector('[data-testid="kanban-stage-empty-action"]')
    ?.focus();
};

const onCreateFunnel = async () => {
  if (!canCreateFunnel.value) return;

  const result = await createFunnel(newFunnelName.value.trim());
  if (!applyResult(result)) return;

  newFunnelName.value = '';
  renameDraft.value = result.funnel.name;
  focusFirstStageAction();
};

const canRename = computed(
  () =>
    Boolean(selectedFunnel.value) &&
    renameDraft.value.trim().length > 0 &&
    renameDraft.value.trim() !== selectedFunnel.value.name &&
    !isSubmitting.value
);

const onRename = async () => {
  if (!canRename.value) return;
  applyResult(
    await renameFunnel(selectedFunnel.value.id, renameDraft.value.trim())
  );
};

const onArchive = async () => {
  applyResult(await archiveFunnel(selectedFunnel.value.id));
};

const onUnarchive = async () => {
  applyResult(await unarchiveFunnel(selectedFunnel.value.id));
};

// 409 aqui só significa funnel_has_cards, e a saída oferecida é ARQUIVAR:
// excluir cascatearia sobre todos os cards, e o card é o único registro de
// onde a conversa está.
const onDelete = async () => {
  const result = await deleteFunnel(selectedFunnel.value.id);
  if (applyResult(result)) renameDraft.value = selectedFunnel.value?.name ?? '';
};

const showArchiveOffer = computed(() => errorKey.value === 'FUNNEL_HAS_CARDS');

const stageCountLabel = funnel =>
  t('KANBAN.ADMIN.FUNNELS.STAGE_COUNT', { count: funnel.stages?.length ?? 0 });
</script>

<template>
  <TeleportWithDirection to="body">
    <!-- `fixed inset-0`: a sobreposição não entra em
         documentElement.scrollWidth, então ela não pode reintroduzir o estouro
         horizontal já medido neste board (1.4.10 Reflow). -->
    <div
      class="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-hidden bg-n-alpha-black2"
      @click="close"
    >
      <div
        ref="dialogRef"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        tabindex="-1"
        data-testid="kanban-funnel-manager"
        class="flex flex-col w-full max-w-2xl min-h-0 overflow-hidden border shadow-xl outline-none max-h-[85vh] rounded-xl border-n-weak bg-n-solid-1"
        @click.stop
      >
        <div
          class="flex items-start justify-between gap-3 px-4 py-3 border-b shrink-0 border-n-weak"
        >
          <!-- h2: o diálogo é teleportado para o fim do body, depois do h1 da
               página; as seções internas seguem em h3. -->
          <h2 :id="titleId" class="m-0 text-base font-semibold text-n-slate-12">
            {{ t('KANBAN.ADMIN.TITLE') }}
          </h2>
          <NextButton
            slate
            ghost
            xs
            icon="i-lucide-x"
            data-testid="kanban-funnel-manager-close"
            :aria-label="t('KANBAN.ADMIN.CLOSE')"
            @click="close"
          />
        </div>

        <div
          ref="bodyRef"
          class="flex flex-col flex-1 min-h-0 gap-4 p-4 overflow-y-auto"
        >
          <p v-if="isLoading" role="status" class="m-0 text-sm text-n-slate-11">
            {{ t('KANBAN.ADMIN.LOADING') }}
          </p>

          <div
            v-else-if="hasLoadError"
            role="alert"
            class="flex items-center gap-2"
          >
            <p class="m-0 text-sm text-n-slate-11">
              {{ t('KANBAN.ADMIN.LOAD_ERROR') }}
            </p>
            <NextButton
              slate
              faded
              xs
              :label="t('KANBAN.ADMIN.RETRY')"
              @click="load"
            />
          </div>

          <template v-else>
            <section class="flex flex-col gap-2">
              <h3 class="m-0 text-sm font-semibold text-n-slate-12">
                {{ t('KANBAN.ADMIN.FUNNELS.SECTION_TITLE') }}
              </h3>
              <p v-if="!funnels.length" class="m-0 text-sm text-n-slate-11">
                {{ t('KANBAN.ADMIN.FUNNELS.EMPTY') }}
              </p>
              <ul v-else class="flex flex-col gap-1 m-0 p-0 list-none">
                <li v-for="funnel in funnels" :key="funnel.id">
                  <button
                    type="button"
                    data-testid="kanban-funnel-option"
                    class="flex items-center justify-between w-full gap-2 px-2 py-1.5 text-left rounded-lg hover:bg-n-alpha-1"
                    :class="
                      funnel.id === selectedFunnelId
                        ? 'bg-n-alpha-2 outline outline-1 -outline-offset-1 outline-n-weak'
                        : ''
                    "
                    :aria-current="funnel.id === selectedFunnelId"
                    @click="onSelectFunnel(funnel)"
                  >
                    <span class="min-w-0 text-sm truncate text-n-slate-12">
                      {{ funnel.name }}
                    </span>
                    <span class="flex items-center gap-2 shrink-0">
                      <span
                        v-if="funnel.archived_at"
                        class="px-1.5 py-0.5 text-xs rounded text-n-slate-12 bg-n-alpha-2"
                      >
                        {{ t('KANBAN.ADMIN.FUNNELS.ARCHIVED_BADGE') }}
                      </span>
                      <span class="text-xs text-n-slate-11">
                        {{ stageCountLabel(funnel) }}
                      </span>
                    </span>
                  </button>
                </li>
              </ul>
            </section>

            <section class="flex flex-col gap-2">
              <h3 class="m-0 text-sm font-semibold text-n-slate-12">
                {{ t('KANBAN.ADMIN.CREATE_FUNNEL.TITLE') }}
              </h3>
              <div class="flex items-end gap-2">
                <Input
                  id="kanban-new-funnel-name"
                  v-model="newFunnelName"
                  size="sm"
                  maxlength="255"
                  class="flex-1"
                  :label="t('KANBAN.ADMIN.CREATE_FUNNEL.NAME_LABEL')"
                  :placeholder="t('KANBAN.ADMIN.CREATE_FUNNEL.PLACEHOLDER')"
                  @enter="onCreateFunnel"
                />
                <!-- !text-n-blue-12: ver a justificativa medida em
                     KanbanDebthubLinkPanel.vue. -->
                <NextButton
                  blue
                  faded
                  xs
                  class="!text-n-blue-12"
                  data-testid="kanban-create-funnel"
                  :disabled="!canCreateFunnel"
                  :label="t('KANBAN.ADMIN.CREATE_FUNNEL.SUBMIT')"
                  @click="onCreateFunnel"
                />
              </div>
            </section>

            <section v-if="selectedFunnel" class="flex flex-col gap-3">
              <div class="flex items-end gap-2">
                <Input
                  id="kanban-funnel-rename"
                  v-model="renameDraft"
                  size="sm"
                  maxlength="255"
                  class="flex-1"
                  :label="t('KANBAN.ADMIN.FUNNEL.RENAME_LABEL')"
                  @enter="onRename"
                />
                <NextButton
                  slate
                  faded
                  xs
                  :disabled="!canRename"
                  :label="t('KANBAN.ADMIN.FUNNEL.RENAME_SUBMIT')"
                  @click="onRename"
                />
              </div>

              <div class="flex flex-wrap items-center gap-2">
                <NextButton
                  v-if="selectedFunnel.archived_at"
                  slate
                  faded
                  xs
                  data-testid="kanban-funnel-unarchive"
                  :disabled="isSubmitting"
                  :label="t('KANBAN.ADMIN.FUNNEL.UNARCHIVE')"
                  @click="onUnarchive"
                />
                <NextButton
                  v-else
                  slate
                  faded
                  xs
                  data-testid="kanban-funnel-archive"
                  :disabled="isSubmitting"
                  :label="t('KANBAN.ADMIN.FUNNEL.ARCHIVE')"
                  @click="onArchive"
                />
                <NextButton
                  ruby
                  ghost
                  xs
                  data-testid="kanban-funnel-delete"
                  :disabled="isSubmitting"
                  :label="t('KANBAN.ADMIN.FUNNEL.DELETE')"
                  @click="onDelete"
                />
              </div>

              <p
                v-if="selectedFunnel.archived_at"
                class="m-0 text-xs text-n-slate-11"
              >
                {{ t('KANBAN.ADMIN.FUNNEL.ARCHIVED_NOTICE') }}
              </p>

              <!-- Funil arquivado não recebe escrita de etapa (o backend
                   responde 422 funnel_archived): a lista sai da tela em vez de
                   oferecer botões que sempre falhariam. -->
              <KanbanStageList
                v-else
                :funnel="selectedFunnel"
                :is-submitting="isSubmitting"
                :create-stage="onCreateStage"
                :update-stage="onUpdateStage"
                :delete-stage="onDeleteStage"
                :reorder-stages="onReorderStages"
              />
            </section>

            <div v-if="errorKey" role="alert" class="flex flex-col gap-1">
              <p class="m-0 flex items-start gap-1 text-xs text-n-ruby-11">
                <span
                  aria-hidden="true"
                  class="i-lucide-triangle-alert size-3 shrink-0 mt-0.5"
                />
                {{
                  t(`KANBAN.ADMIN.ERRORS.${errorKey}`, {
                    count: errorCardsCount ?? 0,
                  })
                }}
              </p>
              <NextButton
                v-if="showArchiveOffer"
                slate
                faded
                xs
                class="self-start"
                data-testid="kanban-funnel-archive-offer"
                :disabled="isSubmitting"
                :label="t('KANBAN.ADMIN.FUNNEL.ARCHIVE')"
                @click="onArchive"
              />
            </div>
          </template>
        </div>
      </div>
    </div>
  </TeleportWithDirection>
</template>
